/**
 * Everything the screen shows about a plan is derived here, from the v2 PlanView and
 * the name catalogs, as pure functions. No Angular, no I/O: this is the part worth
 * unit testing, and keeping it free of the framework is what makes that cheap.
 */
import {
  IsoDate,
  PlanCounts,
  PlanCreationOrigin,
  PlanItemView,
  PlanKind,
  PlanOperation,
  PlanStatus,
  PlanView,
  RbaRunView
} from '../data/plan-management.models';

// ---------------------------------------------------------------------------
// Calendar dates
// ---------------------------------------------------------------------------

/**
 * A `DateOnly` from the server as a local-midnight Date. `new Date("2026-12-24")`
 * is UTC midnight, which renders as the 23rd at UTC-3: the exact drift legacy
 * plan dates suffer from. Build the date from its parts instead.
 */
export function parseIsoDate(value: IsoDate | null | undefined): Date | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value ?? '');
  // DateOnly.MinValue ("0001-01-01") is the server's "no date". Check before building:
  // the Date constructor maps years 0-99 to 1900-1999.
  if (!match || +match[1] < 1000) return null;
  return new Date(+match[1], +match[2] - 1, +match[3]);
}

/** A local calendar day as "yyyy-MM-dd", read from local parts (never `toISOString`). */
export function toIsoDate(date: Date): IsoDate {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

/** A recorded moment, and whether the writer recorded only a day. */
export interface Moment {
  date: Date;
  dayOnly: boolean;
}

/**
 * A moment from the server. Some writers record a day rather than a moment (an import's
 * measurement day, a repair's executed day from ThreeD, migrated dates), stored as that
 * day's UTC midnight. Read in local time it would show as the evening before west of
 * UTC, so it reads as its own day, without a time.
 */
export function parseMoment(value: string | null | undefined): Moment | null {
  const date = value ? new Date(value) : null;
  if (!date || isNaN(date.getTime())) return null;
  const dayOnly =
    date.getUTCHours() === 0 && date.getUTCMinutes() === 0 && date.getUTCSeconds() === 0 && date.getUTCMilliseconds() === 0;
  if (!dayOnly) return { date, dayOnly };
  const day = new Date(0);
  // setFullYear, not the constructor: it maps years 0-99 to 1900-1999.
  day.setFullYear(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate());
  day.setHours(0, 0, 0, 0);
  return { date: day, dayOnly };
}

export function startOfToday(now: Date = new Date()): Date {
  return new Date(now.getFullYear(), now.getMonth(), now.getDate());
}

/** Whole calendar days from `from` to `to` (negative when `to` is earlier). */
export function addDays(date: Date, days: number): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate() + days);
}

export function daysBetween(from: Date, to: Date): number {
  const utc = (d: Date) => Date.UTC(d.getFullYear(), d.getMonth(), d.getDate());
  return Math.round((utc(to) - utc(from)) / 86_400_000);
}

/** "today", "in 3 days", "2 months ago". */
export function relativeDay(date: Date | null, today: Date): string {
  if (!date) return '';
  const days = daysBetween(today, date);
  if (days === 0) return 'today';
  if (days === 1) return 'tomorrow';
  if (days === -1) return 'yesterday';

  const abs = Math.abs(days);
  const [n, unit] =
    abs < 14 ? [abs, 'day'] :
    abs < 60 ? [Math.round(abs / 7), 'week'] :
    abs < 730 ? [Math.round(abs / 30.44), 'month'] :
    [Math.round(abs / 365.25), 'year'];
  const phrase = `${n} ${unit}${n === 1 ? '' : 's'}`;
  return days < 0 ? `${phrase} ago` : `in ${phrase}`;
}

// ---------------------------------------------------------------------------
// Names
// ---------------------------------------------------------------------------

/**
 * Names the plan view does not carry, resolved from the existing catalogs. Each one
 * arrives on its own and the board renders before any of them: `null` means "not
 * loaded yet", which shows as a placeholder rather than as "Unknown".
 */
export interface NameCatalog {
  regions: ReadonlyMap<string, string> | null;
  /** Action id -> action name (`api/Action?assetId=`, slow; only needed for plans without a snapshot). */
  actions: ReadonlyMap<string, string> | null;
  /** Inspection type id -> name (admin CrudBasicDomains). */
  inspectionTypes: ReadonlyMap<string, string> | null;
}

/** Every catalog loaded, and empty. */
export const EMPTY_CATALOG: NameCatalog = {
  regions: new Map(),
  actions: new Map(),
  inspectionTypes: new Map()
};

/** Nothing loaded yet. */
export const PENDING_CATALOG: NameCatalog = {
  regions: null,
  actions: null,
  inspectionTypes: null
};

/** Shown while a name's catalog is still on its way. */
export const PENDING_NAME = '…';

/**
 * `Name` from the plan's InterventionSnapshot. RBA-created plans carry the action (or
 * inspection type) as JSON; migrated plans carry nothing, and some writers store the
 * literal "NotProvided".
 */
export function snapshotName(snapshot: string | null | undefined): string | null {
  if (!snapshot || snapshot[0] !== '{') return null;
  try {
    const parsed = JSON.parse(snapshot) as Record<string, unknown>;
    const name = parsed['Name'] ?? parsed['name'];
    return typeof name === 'string' && name.trim() ? name.trim() : null;
  } catch {
    return null;
  }
}

/**
 * The intervention's name: the snapshot first (it is what the plan was created
 * for), then the live catalog by `ExactInterventionIdentity`. An Action Plan's
 * identity is an action id, except for an RBA "improve inspection" recommendation,
 * whose identity is an inspection type, so both catalogs are consulted.
 */
export function interventionName(plan: PlanView, catalog: NameCatalog): string | null {
  const id = plan.ExactInterventionIdentity;
  return (
    snapshotName(plan.InterventionSnapshot) ??
    (plan.Kind === 'Periodic'
      ? catalog.inspectionTypes?.get(id)
      : catalog.actions?.get(id) ?? catalog.inspectionTypes?.get(id)) ??
    null
  );
}

/** A catalog that could still name this plan has not answered yet. */
function namePending(plan: PlanView, catalog: NameCatalog): boolean {
  return plan.Kind === 'Periodic'
    ? catalog.inspectionTypes === null
    : catalog.actions === null || catalog.inspectionTypes === null;
}

/** Whether this plan can only be named through the (slow) actions catalog. */
export function needsActionCatalog(plan: PlanView): boolean {
  return plan.Kind === 'Action' && !snapshotName(plan.InterventionSnapshot);
}

/** The intervention alone: "Renew", "Thickness Gauging". */
export function planName(plan: PlanView, catalog: NameCatalog): string {
  const name = interventionName(plan, catalog) ?? (namePending(plan, catalog) ? PENDING_NAME : null);
  switch (plan.Kind) {
    case 'Action':
      return name ?? 'Unknown action';
    case 'Periodic':
      return name ?? 'Unknown inspection type';
    default:
      return 'Legacy mixed plan';
  }
}

/** How a plan is titled on screen. */
export function planTitle(plan: PlanView, catalog: NameCatalog): string {
  const name = planName(plan, catalog);
  return plan.Kind === 'Action' ? `Action on Critical Area / ${name}` : name;
}

export function regionName(regionId: string | null | undefined, catalog: NameCatalog): string {
  if (!regionId) return 'No region';
  if (catalog.regions === null) return PENDING_NAME;
  return catalog.regions.get(regionId) ?? 'Unknown region';
}

// ---------------------------------------------------------------------------
// Labels
// ---------------------------------------------------------------------------

export type Tone = 'neutral' | 'info' | 'progress' | 'warning' | 'success' | 'muted' | 'danger';

export interface StatusMeta {
  label: string;
  tone: Tone;
  /** Still an open commitment (not completed, cancelled or superseded). */
  active: boolean;
}

const STATUS: Record<PlanStatus, StatusMeta> = {
  Unstarted: { label: 'Not started', tone: 'info', active: true },
  Started: { label: 'In progress', tone: 'progress', active: true },
  CompletionPending: { label: 'Awaiting approval', tone: 'warning', active: true },
  Completed: { label: 'Completed', tone: 'success', active: false },
  Cancelled: { label: 'Cancelled', tone: 'muted', active: false },
  Superseded: { label: 'Superseded', tone: 'muted', active: false }
};

export function statusMeta(status: PlanStatus): StatusMeta {
  return STATUS[status] ?? { label: status, tone: 'neutral', active: false };
}

export function kindLabel(kind: PlanKind): string {
  return kind === 'Periodic' ? 'Periodic' : kind === 'Action' ? 'Action' : 'Legacy';
}

export function originLabel(origin: PlanCreationOrigin): string {
  switch (origin) {
    case 'Rba': return 'RBA';
    case 'Recurrence': return 'Recurrence';
    case 'Legacy': return 'Migrated';
    case 'UserInitiated': return 'Created by a user';
    default: return origin;
  }
}

/** What a plan's work is called on screen: a plan holds one kind of work. */
export function workNoun(kind: PlanKind): { one: string; many: string; title: string } {
  switch (kind) {
    case 'Periodic': return { one: 'inspection', many: 'inspections', title: 'Inspections' };
    case 'Action': return { one: 'action', many: 'actions', title: 'Actions' };
    default: return { one: 'inspection or action', many: 'inspections and actions', title: 'Work' };
  }
}

/** "P6M" -> "Every 6 months"; migrated day periods also show their month equivalent. */
export function recurrenceLabel(rule: string | null | undefined): string | null {
  const match = /^P(\d+)([YMD])$/i.exec((rule ?? '').trim());
  if (!match) return null;
  const n = +match[1];
  const unit = match[2].toUpperCase();
  if (n <= 0) return null;

  if (unit === 'Y') return n === 1 ? 'Every year' : `Every ${n} years`;
  if (unit === 'M') return n === 1 ? 'Every month' : n % 12 === 0 ? `Every ${n / 12} years` : `Every ${n} months`;

  const months = Math.round(n / 30.44);
  return months >= 1 ? `Every ${n} days (≈ ${months} month${months === 1 ? '' : 's'})` : `Every ${n} days`;
}

/** A recurrence rule as the command expects it: whole months or years. */
export function recurrenceRule(value: number, unit: 'M' | 'Y'): string {
  return `P${Math.max(1, Math.floor(value))}${unit}`;
}

/** What kind of work one plan entry is, from which identity it carries. */
export function itemWorkLabel(item: PlanItemView): string {
  if (item.RepairId) return 'Repair';
  if (item.ProtectionId) return 'Protection';
  if (item.InspectionOccurrenceId || item.InspectionElementId) return 'Inspection';
  if (item.ActionOccurrenceId || item.CriticalAreaId) return 'Critical Area action';
  return 'Work';
}

// ---------------------------------------------------------------------------
// Progress and urgency
// ---------------------------------------------------------------------------

export interface PlanProgress {
  /** Work still committed: everything except cancelled entries. */
  planned: number;
  executed: number;
  skipped: number;
  outlined: number;
  cancelled: number;
  /** Bar segments, as whole percentages of `planned`. */
  executedPct: number;
  skippedPct: number;
}

/** Straight from the server's counts; the client never recounts plan scope. */
export function planProgress(counts: PlanCounts | null | undefined): PlanProgress {
  const c = counts ?? { Total: 0, Outlined: 0, Executed: 0, Skipped: 0, Cancelled: 0 };
  const planned = Math.max(0, c.Total - c.Cancelled);
  const pct = (n: number) => (planned ? Math.round((n / planned) * 100) : 0);
  const executedPct = pct(c.Executed);
  return {
    planned,
    executed: c.Executed,
    skipped: c.Skipped,
    outlined: c.Outlined,
    cancelled: c.Cancelled,
    executedPct,
    // Rounded separately, so clamp: the two segments never overflow the bar.
    skippedPct: Math.min(100 - executedPct, pct(c.Skipped))
  };
}

/** Still being worked on after its window closed. Awaiting approval is not overdue. */
export function isOverdue(plan: PlanView, today: Date): boolean {
  if (plan.Status !== 'Unstarted' && plan.Status !== 'Started') return false;
  const end = parseIsoDate(plan.WindowEnd);
  return !!end && daysBetween(today, end) < 0;
}

export function can(plan: PlanView, operation: PlanOperation): boolean {
  return plan.AllowedOperations?.includes(operation) ?? false;
}

// ---------------------------------------------------------------------------
// The board
// ---------------------------------------------------------------------------

export interface PlanCard {
  plan: PlanView;
  title: string;
  /** The intervention alone, without the "Action on Critical Area /" prefix. */
  name: string;
  regionName: string;
  status: StatusMeta;
  kind: string;
  origin: string;
  noun: ReturnType<typeof workNoun>;
  progress: PlanProgress;
  target: Date | null;
  windowStart: Date | null;
  windowEnd: Date | null;
  recurrence: string | null;
  overdue: boolean;
  /** Lower-cased text the search box matches against. */
  searchText: string;
}

export function toPlanCard(plan: PlanView, catalog: NameCatalog, today: Date): PlanCard {
  const title = planTitle(plan, catalog);
  const region = regionName(plan.RegionId, catalog);
  return {
    plan,
    title,
    name: planName(plan, catalog),
    regionName: region,
    status: statusMeta(plan.Status),
    kind: kindLabel(plan.Kind),
    origin: originLabel(plan.CreationOrigin),
    noun: workNoun(plan.Kind),
    progress: planProgress(plan.Counts),
    target: parseIsoDate(plan.TargetDate),
    windowStart: parseIsoDate(plan.WindowStart),
    windowEnd: parseIsoDate(plan.WindowEnd),
    recurrence: recurrenceLabel(plan.RecurrenceRule),
    overdue: isOverdue(plan, today),
    searchText: `${title} ${region}`.toLowerCase()
  };
}

export type StatusFilter = 'active' | 'closed' | 'all';
export type KindFilter = 'all' | PlanKind;

export interface BoardFilter {
  status: StatusFilter;
  kind: KindFilter;
  regionId: string | null;
  search: string;
}

export function matchesFilter(card: PlanCard, filter: BoardFilter): boolean {
  if (filter.status === 'active' && !card.status.active) return false;
  if (filter.status === 'closed' && card.status.active) return false;
  if (filter.kind !== 'all' && card.plan.Kind !== filter.kind) return false;
  if (filter.regionId !== null && (card.plan.RegionId ?? '') !== filter.regionId) return false;
  const q = filter.search.trim().toLowerCase();
  return !q || card.searchText.includes(q);
}

export interface RegionGroup {
  regionId: string;
  regionName: string;
  cards: PlanCard[];
}

/** Open work first, then by target date; regions alphabetically. */
export function groupByRegion(cards: readonly PlanCard[]): RegionGroup[] {
  const groups = new Map<string, RegionGroup>();
  for (const card of cards) {
    const id = card.plan.RegionId ?? '';
    let group = groups.get(id);
    if (!group) {
      group = { regionId: id, regionName: card.regionName, cards: [] };
      groups.set(id, group);
    }
    group.cards.push(card);
  }

  const time = (d: Date | null) => d?.getTime() ?? Number.MAX_SAFE_INTEGER;
  for (const group of groups.values()) {
    group.cards.sort(
      (a, b) =>
        Number(b.status.active) - Number(a.status.active) ||
        time(a.target) - time(b.target) ||
        a.title.localeCompare(b.title)
    );
  }
  return [...groups.values()].sort((a, b) => a.regionName.localeCompare(b.regionName));
}

// ---------------------------------------------------------------------------
// Commands
// ---------------------------------------------------------------------------

export type PlanCommand = 'complete' | 'review' | 'reschedule' | 'recurrence' | 'action' | 'cancel';

/** The single-plan changes handled by the change dialog; closeout and Change action have their own. */
export type ChangeCommand = Exclude<PlanCommand, 'complete' | 'review' | 'action'>;

export function isChangeCommand(command: PlanCommand): command is ChangeCommand {
  return command === 'reschedule' || command === 'recurrence' || command === 'cancel';
}

export interface PlanCommandOption {
  id: PlanCommand;
  label: string;
  icon: string;
  primary?: boolean;
  danger?: boolean;
}

/** What the user's permissions allow on an asset's plans. */
export interface PlanAccess {
  /** The Action Center: asking for a plan's closeout. */
  actionCenter: boolean;
  /** Approve Schedule: reviewing closeouts and changing plans. */
  approveSchedule: boolean;
}

export const NO_ACCESS: PlanAccess = { actionCenter: false, approveSchedule: false };

/**
 * What the user can do with a plan right now: the server's AllowedOperations,
 * within the user's access. Completing comes first: it is what an open plan is for.
 */
export function planCommands(plan: PlanView, access: PlanAccess): PlanCommandOption[] {
  const list: PlanCommandOption[] = [];
  if (access.actionCenter && can(plan, 'SubmitPlanCloseout')) {
    list.push({ id: 'complete', label: 'Complete', icon: 'pi pi-check-circle', primary: true });
  }
  if (!access.approveSchedule) return list;
  if (plan.ActiveCloseoutSubmissionId && (can(plan, 'ApprovePlanCloseout') || can(plan, 'RejectPlanCloseout'))) {
    list.push({ id: 'review', label: 'Review closeout', icon: 'pi pi-verified', primary: true });
  }
  if (can(plan, 'ReschedulePlan')) list.push({ id: 'reschedule', label: 'Reschedule', icon: 'pi pi-calendar' });
  if (can(plan, 'ChangePlanRecurrence')) list.push({ id: 'recurrence', label: 'Recurrence', icon: 'pi pi-replay' });
  if (canChangeAction(plan)) list.push({ id: 'action', label: 'Change action', icon: 'pi pi-sort-alt' });
  if (can(plan, 'CancelPlan')) list.push({ id: 'cancel', label: 'Cancel', icon: 'pi pi-ban', danger: true });
  return list;
}

/**
 * Only an Action Plan RBA created changes action (the server advertises
 * ReplaceActionPlanItems on those), and from this screen only while none of its work is
 * executed: the change switches every area of the plan at once (one area at a time
 * belongs to the 3D Critical Area window).
 */
export function canChangeAction(plan: PlanView): boolean {
  return (
    plan.Kind === 'Action' &&
    can(plan, 'ReplaceActionPlanItems') &&
    !plan.ActiveCloseoutSubmissionId &&
    plan.Counts.Executed === 0 &&
    plan.Counts.Outlined > 0
  );
}

// ---------------------------------------------------------------------------
// Closeout
// ---------------------------------------------------------------------------

/**
 * Plans that may join `plan` in one Closeout Submission: the server takes one
 * asset, one region and one kind per submission, and never a plan already in one.
 * The given plan comes first.
 */
export function closeoutCandidates(plan: PlanView, all: readonly PlanView[]): PlanView[] {
  const eligible = (p: PlanView) =>
    can(p, 'SubmitPlanCloseout') &&
    !p.ActiveCloseoutSubmissionId &&
    p.Kind === plan.Kind &&
    (p.RegionId ?? '') === (plan.RegionId ?? '') &&
    p.AssetId === plan.AssetId;
  return [plan, ...all.filter(p => p.PlanId !== plan.PlanId && eligible(p))];
}

export interface CloseoutImpact {
  plans: number;
  /** Outlined work that approval will mark Skipped. */
  toSkip: number;
  executed: number;
  /** Plans that will get a next cycle on approval. */
  recurring: number;
  kind: PlanKind;
}

export function closeoutImpact(plans: readonly PlanView[]): CloseoutImpact {
  return {
    plans: plans.length,
    toSkip: plans.reduce((n, p) => n + (p.Counts?.Outlined ?? 0), 0),
    executed: plans.reduce((n, p) => n + (p.Counts?.Executed ?? 0), 0),
    recurring: plans.filter(p => !!recurrenceLabel(p.RecurrenceRule)).length,
    kind: plans[0]?.Kind ?? 'Periodic'
  };
}

/** The revisions a command must carry, as the user saw them. */
export function revisionsOf(plans: readonly PlanView[]): Record<string, number> {
  return Object.fromEntries(plans.map(p => [p.PlanId, p.Revision]));
}

// ---------------------------------------------------------------------------
// RBA run
// ---------------------------------------------------------------------------

export interface RbaOutcome {
  finished: boolean;
  failed: boolean;
  headline: string;
  detail: string | null;
}

export function rbaOutcome(run: RbaRunView): RbaOutcome {
  const actions = run.PrescribedActions ?? [];
  const count = (d: string) => actions.filter(a => a.Disposition === d).length;
  switch (run.Status) {
    case 'AwaitingFEA':
      return { finished: false, failed: false, headline: 'RBA is waiting for FEA results', detail: null };
    case 'Pending':
      return { finished: false, failed: false, headline: 'RBA is running', detail: null };
    case 'Failed':
      return { finished: true, failed: true, headline: 'RBA failed', detail: run.LastErrorDetail ?? run.LastErrorCode };
    default: {
      if (!actions.length) {
        return { finished: true, failed: false, headline: 'RBA finished: no actions prescribed', detail: null };
      }
      const parts = [
        count('Materialized') && `${count('Materialized')} became Action Plans`,
        count('NoExecutableScope') && `${count('NoExecutableScope')} had nothing executable`,
        count('Unmaterialized') && `${count('Unmaterialized')} could not be created yet`,
        count('Pending') && `${count('Pending')} still being created`
      ].filter(Boolean);
      return {
        finished: true,
        failed: false,
        headline: `RBA finished: ${actions.length} action${actions.length === 1 ? '' : 's'} prescribed`,
        detail: parts.join(' · ') || null
      };
    }
  }
}
